import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetchMediaList } from '../api';
import { MediaItem } from '../types';

interface UseOptimisticMediaParams {
  accountId: string;
  mediaTypeFilter: string; // 'all' | 'images' | 'videos'
  identityFilter?: string;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  searchQuery?: string;
}

export function useOptimisticMedia(params: UseOptimisticMediaParams) {
  // Map table params to useFetchMediaList params
  const apiParams = useMemo(() => {
    return {
      account_id: params.accountId,
      limit: 25,
      ordering: `${params.sortDirection === 'desc' ? '-' : ''}${params.sortBy}`,
      ...(params.searchQuery && { query: params.searchQuery }),
      ...(params.identityFilter && { identity_id: params.identityFilter }),
      ...(params.mediaTypeFilter !== 'all' && {
        media_type:
          params.mediaTypeFilter === 'images' ? ('IMAGE' as const) : ('VIDEO' as const),
      }),
    };
  }, [
    params.accountId,
    params.sortDirection,
    params.sortBy,
    params.searchQuery,
    params.identityFilter,
    params.mediaTypeFilter,
  ]);

  const {
    data: serverData,
    isLoading: serverLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchMediaList(apiParams, !!params.accountId);

  // Track previous successful data
  const previousDataRef = useRef<MediaItem[]>([]);
  const previousParamsRef = useRef<UseOptimisticMediaParams | null>(null);
  const [_isOptimisticFiltering, setIsOptimisticFiltering] = useState(false);

  // Process infinite query data into flat array
  const processedServerData = useMemo(() => {
    if (!serverData?.pages) return [];
    return serverData.pages.flatMap((page) => page.data.results);
  }, [serverData]);

  // Update previous data when we get new server data
  useEffect(() => {
    if (!serverLoading && processedServerData.length > 0) {
      previousDataRef.current = processedServerData;
      setIsOptimisticFiltering(false);
    }
  }, [processedServerData, serverLoading]);

  // Check if params changed (indicating a new filter/search)
  const paramsChanged = useMemo(() => {
    if (!previousParamsRef.current) return false;

    const prev = previousParamsRef.current;
    return (
      prev.mediaTypeFilter !== params.mediaTypeFilter ||
      prev.searchQuery !== params.searchQuery ||
      prev.identityFilter !== params.identityFilter ||
      prev.sortBy !== params.sortBy ||
      prev.sortDirection !== params.sortDirection ||
      prev.accountId !== params.accountId
    );
  }, [
    params.mediaTypeFilter,
    params.searchQuery,
    params.identityFilter,
    params.sortBy,
    params.sortDirection,
    params.accountId,
  ]);

  // Update params ref
  useEffect(() => {
    previousParamsRef.current = params;
  }, [params]);

  // Client-side filtering logic
  const applyClientSideFilters = useMemo(() => {
    return (data: MediaItem[]) => {
      let filtered = [...data];

      // Apply media type filtering
      if (params.mediaTypeFilter !== 'all') {
        const targetType = params.mediaTypeFilter === 'images' ? 'IMAGE' : 'VIDEO';
        filtered = filtered.filter((item) => item.media_type === targetType);
      }

      // Apply identity filtering
      if (params.identityFilter) {
        filtered = filtered.filter((item) => item.identity_id === params.identityFilter);
      }

      // Apply search filtering (search in title)
      if (params.searchQuery && params.searchQuery.trim()) {
        const searchTerm = params.searchQuery.trim().toLowerCase();
        filtered = filtered.filter((item) =>
          item.title.toLowerCase().includes(searchTerm)
        );
      }

      // Apply sorting
      filtered = [...filtered].sort((a, b) => {
        let valueA: unknown;
        let valueB: unknown;

        // Handle different sort fields
        switch (params.sortBy) {
          case 'created_date':
            valueA = new Date(a.created_date).getTime();
            valueB = new Date(b.created_date).getTime();
            break;
          case 'last_touched_date':
            valueA = new Date(a.last_touched_date).getTime();
            valueB = new Date(b.last_touched_date).getTime();
            break;
          case 'title':
            valueA = a.title.toLowerCase();
            valueB = b.title.toLowerCase();
            break;
          case 'size_bytes':
            valueA = a.size_bytes;
            valueB = b.size_bytes;
            break;
          default:
            // Default to created_date if unknown sort field
            valueA = new Date(a.created_date).getTime();
            valueB = new Date(b.created_date).getTime();
        }

        // Apply sort direction
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return params.sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
        }

        // String comparison
        const compareResult = String(valueA).localeCompare(String(valueB));
        return params.sortDirection === 'asc' ? compareResult : -compareResult;
      });

      return filtered;
    };
  }, [
    params.mediaTypeFilter,
    params.identityFilter,
    params.searchQuery,
    params.sortBy,
    params.sortDirection,
  ]);

  // Determine if we should show optimistic data
  const shouldShowOptimistic = useMemo(() => {
    return (
      paramsChanged && serverLoading && previousDataRef.current.length > 0
      // Note: Unlike jobs/optouts, media doesn't have complex server-side only filters
      // so we can show optimistic data for all filter types
    );
  }, [paramsChanged, serverLoading]);

  // Set optimistic filtering state
  useEffect(() => {
    if (shouldShowOptimistic) {
      setIsOptimisticFiltering(true);
    }
  }, [shouldShowOptimistic]);

  // Calculate final data to show
  const displayData = useMemo(() => {
    if (shouldShowOptimistic) {
      // Show optimistic filtered data from previous results
      return applyClientSideFilters(previousDataRef.current);
    }

    // Show server data (could be empty during initial load)
    return applyClientSideFilters(processedServerData);
  }, [shouldShowOptimistic, applyClientSideFilters, processedServerData]);

  // Calculate loading states
  const isLoadingOptimistic = shouldShowOptimistic;
  const isInitialLoading = serverLoading && previousDataRef.current.length === 0;

  return {
    data: displayData,
    isLoading: isInitialLoading, // Only show skeleton on initial load
    isOptimisticFiltering: isLoadingOptimistic, // Background loading indicator
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
}
